import { KEYS } from './storage';

export const SUIT_SYMBOLS = { d: '♦️', c: '♣️', h: '♥️', s: '♠️' };

const SUIT_NAMES = { d: 'Diamonds', c: 'Clubs', h: 'Hearts', s: 'Spades' };
const RANK_NAMES = {
    '2': 'Two', '3': 'Three', '4': 'Four', '5': 'Five', '6': 'Six',
    '7': 'Seven', '8': 'Eight', '9': 'Nine', T: 'Ten', J: 'Jack',
    Q: 'Queen', K: 'King', A: 'Ace',
};

export const CARD_VARIANTS = [
    'holo', 
    'antique', 
    'foil', 
    'ghost', 
    'blueprint', 
    'crayon', 
    's500th',
    'cosmic',
    'cutesy',
    'matcha',
    'rurikara',
    'halloween',
];

export const VARIANT_NAMES = {
    holo: 'Holographic',
    antique: 'Antique',
    foil: 'Foil',
    ghost: 'Ghostly',
    blueprint: 'Blueprint',
    crayon: 'Keepsake',
    's500th': '№ 500',
    cosmic: 'Cosmic',
    cutesy: 'Sakura',
    matcha: 'Matcha',
    rurikara: 'Ruri',
    halloween: 'Haunted',
};

export const VARIANT_MESSAGES = {
    's500th': 'Celebrating puzzle #500'
};

// Chance (%) of earning each card. Enter whatever numbers you like, they don't need to add up
// to 100: if they fall short the gap goes to standard, and if they overshoot everything is
// scaled down in proportion. `standard` is the plain card; a variant that is missing or 0
// never drops, and retired variants are excluded regardless.
// run `npm run winnings npm run winnings -- --runs 100000 --out --month 1 /tmp/winnings.json` to simulate winnings for January
export const STANDARD = 'standard';
export const VARIANT_ODDS = {
    [STANDARD]: 50,
    crayon: 4,
    holo: 4,
    antique: 4,
    foil: 4,
    blueprint: 4,
    cosmic: 4,
    cutesy: 4,
    matcha: 4,
    rurikara: 4,
    halloween: 4,
};

// Per-month overrides (0-indexed month), merged over VARIANT_ODDS. Set `standard: 0` to
// replace the plain card entirely for the month.
export const MONTHLY_ODDS = {
    9: { [STANDARD]: 0, halloween: 50 }, // October 
};

// [name, actual chance %] pairs for the month, always totalling 100.
export const getVariantOdds = (now = new Date()) => {
    const odds = { ...VARIANT_ODDS, ...MONTHLY_ODDS[now.getMonth()] };
    const entries = Object.entries(odds).filter(([name, chance]) => (
        chance > 0 && (name === STANDARD || (CARD_VARIANTS.includes(name) && !RETIRED_VARIANTS.includes(name)))
    ));
    const total = entries.reduce((sum, [, chance]) => sum + chance, 0);
    if (total > 100) {
        return entries.map(([name, chance]) => [name, (chance * 100) / total]);
    }
    const shortfall = 100 - total;
    const standard = (odds[STANDARD] > 0 ? odds[STANDARD] : 0) + shortfall;
    return [
        ...(standard > 0 ? [[STANDARD, standard]] : []),
        ...entries.filter(([name]) => name !== STANDARD),
    ];
};

// Retire variants that are too similar to others or don't look good enough
// But may have been earned by players in the past, so we want to keep them in the codebase for now
// If a retired variant is listed in the FORCE_VARIANT_ON_PUZZLE_NUMBER, it will still be used for that puzzle, but won't be randomly assigned otherwise
export const RETIRED_VARIANTS = ['ghost', 's500th'];

// Variants that are no longer supported. Cards already earned still render wherever they
// appear, but these get no collection tab of their own.
export const UNSUPPORTED_VARIANTS = ['ghost'];

// For certain puzzles, force a specific variant to be selected, eg. 500th puzzle
// Format needs to match puzzle.name, eg. #500
export const FORCE_VARIANT_ON_PUZZLE_NUMBER = {
    '#500': 's500th',
};

// Variants only ever awarded on one specific puzzle, so a player can hold a couple at most.
// A full 53-slot binder would be meaningless for these.
export const isOneOffVariant = (variant) => {
    return Object.values(FORCE_VARIANT_ON_PUZZLE_NUMBER).includes(variant);
};

export const humanCardName = (card) => {
    // Handle variants: "holo_4d" -> "4d", variant="holo"
    let baseCard = card;
    let variant = null;

    for (const v of CARD_VARIANTS) {
        if (card.startsWith(`${v}_`)) {
            variant = v;
            baseCard = card.slice(v.length + 1);
            break;
        }
    }

    let name = 'Wild Card';
    if (card === 'or') return 'Wild Card';
    if (baseCard !== 'or') {
        name = `${RANK_NAMES[baseCard.slice(0, -1)]} of ${SUIT_NAMES[baseCard.slice(-1)]}`;
    }

    if (variant) {
        return `${VARIANT_NAMES[variant]} ${name}`;
    }
    return name;
};

export const getBaseCard = (card) => {
    for (const variant of CARD_VARIANTS) {
        if (card.startsWith(`${variant}_`)) {
            return card.slice(variant.length + 1);
        }
    }
    return card;
};

export const getCardVariant = (card) => {
    for (const variant of CARD_VARIANTS) {
        if (card.startsWith(`${variant}_`)) {
            return variant;
        }
    }
    return null;
};

export const randomizeCardVariant = (baseCard, puzzleName, now = new Date(), random = Math.random) => {

    // Check if the current puzzle has a forced variant
    if (puzzleName && FORCE_VARIANT_ON_PUZZLE_NUMBER[puzzleName]) {
        const forcedVariant = FORCE_VARIANT_ON_PUZZLE_NUMBER[puzzleName];
        return `${forcedVariant}_${baseCard}`;
    }

    const entries = getVariantOdds(now);
    const totalWeight = entries.reduce((sum, [, weight]) => sum + weight, 0);
    let roll = random() * totalWeight;
    for (const [name, weight] of entries) {
        roll -= weight;
        if (roll < 0) {
            return name === STANDARD ? baseCard : `${name}_${baseCard}`;
        }
    }
    return baseCard;
};

export const ALL_CARDS = [
    '2h', '3h', '4h', '5h', '6h', '7h', '8h', '9h', 'Th', 'Jh', 'Qh', 'Kh', 'Ah',
    '2d', '3d', '4d', '5d', '6d', '7d', '8d', '9d', 'Td', 'Jd', 'Qd', 'Kd', 'Ad',
    '2c', '3c', '4c', '5c', '6c', '7c', '8c', '9c', 'Tc', 'Jc', 'Qc', 'Kc', 'Ac',
    '2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s', 'Ts', 'Js', 'Qs', 'Ks', 'As',
    'or',
];

export const randomBaseCard = (random = Math.random) => ALL_CARDS[Math.floor(random() * ALL_CARDS.length)] || 'or';

// Contents of one opened vault deck: one guaranteed themed card, then each remaining slot is
// themed at `themedRate`, otherwise rolled off the normal odds table like a daily win.
export const rollDeckRewards = ({ variant, rewardCount, themedRate = 0 }, now = new Date(), random = Math.random) => {
    const guaranteed = Math.min(1, rewardCount);
    const rewards = Array.from({ length: rewardCount }, (_, i) => {
        if (i < guaranteed || random() < themedRate) {
            return `${variant}_${randomBaseCard(random)}`;
        }
        return randomizeCardVariant(randomBaseCard(random), undefined, now, random);
    });
    return rewards.sort(() => random() - 0.5);
};

export const getDisplayCard = (baseCard, playerDeck) => {
    if (!playerDeck) return baseCard;
    
    // Get all variants the player has of this card
    const availableCards = [baseCard]; // Always include base card
    
    for (const variant of CARD_VARIANTS) {
        const variantCard = `${variant}_${baseCard}`;
        if (playerDeck[variantCard]) {
            availableCards.push(variantCard);
        }
    }
    
    // If only base card is available, return it
    if (availableCards.length === 1) {
        return baseCard;
    }
    
    // Use a deterministic hash of the card name to pick a variant
    // This ensures the same card always shows the same variant for consistency
    // Use the puzzle signature so it changes each puzzle, but is consistent within the same puzzle
    let puzzleSignature = parseInt(localStorage.getItem(KEYS.CURRENT_PUZZLE)) || 0;
    let numericSignature = 0;
    for (let i = 0; i < puzzleSignature.toString().length; i++) {
        numericSignature += puzzleSignature.toString().charCodeAt(i);
    }
    let hash = 0;
    for (let i = 0; i < baseCard.length; i++) {
        hash = baseCard.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash + numericSignature) % availableCards.length;
    return availableCards[index];
};

export const COLLECTION_TAB_ALL = 'all';
export const COLLECTION_TAB_STANDARD = 'standard';
export const COLLECTION_TAB_SPECIALS = 'specials';

// Variant cards of one variant that the player owns, in ALL_CARDS order.
export const getOwnedVariantCards = (deck, variant) => {
    return ALL_CARDS
        .map((baseCard) => `${variant}_${baseCard}`)
        .filter((variantCard) => deck[variantCard] > 0);
};

// Every one-off event card the player owns, grouped by variant in CARD_VARIANTS order.
export const getOwnedSpecialCards = (deck) => {
    return CARD_VARIANTS
        .filter(isOneOffVariant)
        .flatMap((variant) => getOwnedVariantCards(deck, variant));
};

// Tabs for the collection grid: all, standard, specials, then CARD_VARIANTS order.
// A variant only earns a tab once the player owns one of it, so discovering a new variant
// stays a surprise instead of being spoiled by a row of empty tabs.
export const getCollectionTabs = (deck) => {
    const specialCards = getOwnedSpecialCards(deck);

    const tabs = [
        { id: COLLECTION_TAB_ALL, label: 'All', count: Object.keys(deck).length },
        { id: COLLECTION_TAB_STANDARD, label: 'Standard', count: ALL_CARDS.filter((card) => deck[card] > 0).length },
    ];

    // One-off event cards share a single tab rather than one tab each.
    if (specialCards.length > 0) {
        tabs.push({ id: COLLECTION_TAB_SPECIALS, label: 'Specials', count: specialCards.length });
    }

    for (const variant of CARD_VARIANTS) {
        if (UNSUPPORTED_VARIANTS.includes(variant)) continue;
        // One-off variants are collected under the Specials tab instead of getting their own.
        if (isOneOffVariant(variant)) continue;
        const ownedCount = getOwnedVariantCards(deck, variant).length;
        if (ownedCount === 0) continue;
        tabs.push({ id: variant, label: VARIANT_NAMES[variant], count: ownedCount });
    }

    return tabs;
};

// Cards to render in the collection grid for the active tab.
// Tabs are full 53-slot binders with unowned cards drawn face down, so a variant tab reads
// the same way the standard one does. One-off event cards are the exception: they only ever
// existed as a handful of cards, so showing face-down slots would misrepresent them as a set.
export const getCollectionCards = (deck, tab) => {
    if (tab === COLLECTION_TAB_STANDARD) {
        return [...ALL_CARDS];
    }

    if (tab === COLLECTION_TAB_SPECIALS) {
        return getOwnedSpecialCards(deck);
    }

    if (isOneOffVariant(tab)) {
        return getOwnedVariantCards(deck, tab);
    }

    if (tab === COLLECTION_TAB_ALL) {
        // Keep the full standard-card binder, and reveal variants right after their base card.
        return ALL_CARDS.flatMap((baseCard) => [
            baseCard,
            ...CARD_VARIANTS
                .map((variant) => `${variant}_${baseCard}`)
                .filter((variantCard) => deck[variantCard] > 0),
        ]);
    }

    return ALL_CARDS.map((baseCard) => `${tab}_${baseCard}`);
};

export const HAND_EXAMPLES = [
    { name: 'Straight Flush', cards: ['qh', 'jh', '9h', 'th', '8h'], desc: 'Five cards of sequential rank, all of the same suit.' },
    { name: 'Four of a kind', cards: ['9c', '9s', '9h', '9d', '2s'], desc: 'Contains four cards of one rank and one card of another rank.' },
    { name: 'Full house', cards: ['7c', '7s', '7h', 'ad', 'as'], desc: 'Three cards of one rank and two cards of another rank.' },
    { name: 'Flush', cards: ['9c', '7c', 'kc', '3c', '4c'], desc: 'Any five cards all of the same suit.' },
    { name: 'Straight', cards: ['9h', '8s', '7h', '6d', '5c'], desc: 'Five cards of sequential rank, not all of the same suit.' },
    { name: 'Three of a kind', cards: ['ah', 'as', 'ad', '8c', 'qh'], desc: 'Contains three cards of one rank and two cards of two other ranks.' },
    { name: 'Two Pair', cards: ['ah', 'as', 'jh', '8c', '8s'], desc: 'Contains two cards of one rank, two cards of another rank and one card of a third rank.' },
];

// Deck trade-in economy -------------------------------------------------------
// Themed decks in the vault are bought with cards out of the player's own collection.
// Only duplicates are ever spent: the first copy of a card is the one filling its slot
// in the binder, so it is never up for trade. That way a trade-in can churn a
// collection but can never shrink it.

// One-off event cards were only ever awarded on a single puzzle, so even a second copy
// is unreplaceable. They stay out of the trade-in economy entirely.
export const isCardSpendable = (card) => {
    const variant = getCardVariant(card);
    if (!variant) return true;
    return !isOneOffVariant(variant);
};

// Copies of one card the player can spend without emptying its collection slot.
export const getSpendableDuplicateCount = (deck, card) => {
    if (!isCardSpendable(card)) return 0;
    return Math.max(0, (deck[card] || 0) - 1);
};

// The player's whole trade-in balance: every duplicate across the collection.
export const getTotalSpendableDuplicates = (deck) => {
    return Object.keys(deck).reduce((sum, card) => sum + getSpendableDuplicateCount(deck, card), 0);
};

// Spend `amount` duplicates picked at random, weighted so a card the player holds more
// spare copies of is likelier to go. Stops early rather than overdrawing if the pool runs
// dry. The input deck is left untouched; the caller persists the returned one.
export const consumeDuplicateCards = (deck, amount, random = Math.random) => {
    const nextDeck = { ...deck };
    const paidCards = [];

    for (let i = 0; i < amount; i++) {
        const entries = Object.keys(nextDeck)
            .map((card) => [card, getSpendableDuplicateCount(nextDeck, card)])
            .filter(([, duplicates]) => duplicates > 0);
        if (entries.length === 0) break;

        const totalWeight = entries.reduce((sum, [, duplicates]) => sum + duplicates, 0);
        let roll = random() * totalWeight;
        let selectedCard = entries[0][0];

        for (const [card, duplicates] of entries) {
            roll -= duplicates;
            if (roll <= 0) {
                selectedCard = card;
                break;
            }
        }

        paidCards.push(selectedCard);
        // Only duplicates are ever selected, so the count can never reach zero here.
        nextDeck[selectedCard] -= 1;
    }

    return { nextDeck, paidCards };
};

// Vault deck cost grows with how much of that variant the player already owns, so the
// first cards of a set come cheap and the last few take real saving up.
export const BASE_DECK_COST = 8;
export const getDeckCost = (deck, variant) => {
    const owned = Object.keys(deck).filter((card) => deck[card] > 0 && getCardVariant(card) === variant).length;
    return BASE_DECK_COST + Math.floor(owned / 6);
};
